import React, { useState } from "react";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CardActions,
  CardMedia,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { IOpenaiDescription } from "@/types/typesAlerts";

interface EvidenceFile {
  content_type: string;
  download_url: string;
  filename: string;
  openai_description?: IOpenaiDescription;
  sort_ordinal?: number;
  thumbnail_url?: string;
}

interface BulletinShareCardProps {
  id: number;
  bulletin_id: number;
  source_user?: {
    rubi_user: {
      first_name: string;
      last_name: string;
    };
  } | null;
  source_org?: { id: string; title: string } | null;
  destination_group?: { id: number; name: string } | null;
  bulletin?: {
    id: number;
    description: string;
    evidence_files?: { [key: string]: EvidenceFile };
  } | null;
  time_expires?: string | null;
  onAdmit?: (id: number) => void;
  onReject?: (id: number) => void;
  is_moderation_enabled?: boolean;
}

const MAX_DESCRIPTION_LENGTH = 120;

const BulletinShareCard: React.FC<BulletinShareCardProps> = ({
  id,
  bulletin_id,
  source_user,
  source_org,
  destination_group,
  bulletin,
  time_expires,
  onAdmit,
  onReject,
  is_moderation_enabled,
}) => {
  const { t } = useTranslation(["bulletin/share", "bulletin"]);
  const [descExpanded, setDescExpanded] = useState(false);
  const navigate = useNavigate();

  // Find the first image or video evidence file (sorted by sort_ordinal or filename)
  let media: EvidenceFile | null = null;
  if (bulletin?.evidence_files) {
    const files = Object.values(bulletin.evidence_files);
    files.sort((a, b) => (a.sort_ordinal ?? 0) - (b.sort_ordinal ?? 0));
    media =
      files.find((f) => f.content_type?.startsWith("image/")) ||
      files.find((f) => f.content_type?.startsWith("video/")) ||
      null;
  }

  const description = bulletin?.description || "-";
  const isLong = description.length > MAX_DESCRIPTION_LENGTH;
  const showDesc = descExpanded || !isLong;

  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: 3,
        boxShadow: 3,
        maxWidth: 340,
        width: "100%",
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        height: 1,
        minHeight: 420,
        cursor: "pointer",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 6,
        },
      }}
      onClick={() => {
        if (is_moderation_enabled) navigate(`/rubialert/bulletin-share/${id}`);
      }}
    >
      {media && media.content_type.startsWith("image/") && (
        <CardMedia
          component="img"
          height="180"
          image={media.thumbnail_url || media.download_url}
          alt={media.filename}
          sx={{
            objectFit: "cover",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        />
      )}
      {media && media.content_type.startsWith("video/") && (
        <CardMedia
          component="video"
          height="180"
          src={media.download_url}
          controls
          sx={{
            objectFit: "cover",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        />
      )}
      {!media && (
        <Box
          sx={{
            height: 180,
            backgroundColor: "grey.100",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "grey.400",
            fontSize: 48,
          }}
        >
          {/* Optionally, you can use an icon or just leave it blank */}
        </Box>
      )}
      <CardContent
        sx={{ flex: 1, display: "flex", flexDirection: "column", pb: 1 }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: "text.secondary" }}
          gutterBottom
        >
          {t("bulletin_id")}: {bulletin_id}
        </Typography>
        <Box sx={{ mb: 1, minHeight: 48 }}>
          <Typography
            variant="body2"
            sx={{ display: "inline" }}
            component="span"
          >
            {showDesc
              ? description
              : description.slice(0, MAX_DESCRIPTION_LENGTH) + "..."}
          </Typography>
          {isLong && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDescExpanded((v) => !v);
              }}
              sx={{ ml: 1, verticalAlign: "middle" }}
              aria-label={
                descExpanded
                  ? t("Collapse description")
                  : t("Expand description")
              }
            >
              {descExpanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1 }}>
          <Typography variant="body2">
            <strong>{t("from")}:</strong>{" "}
            {t("from_user", {
              firstName: source_user?.rubi_user?.first_name,
              lastName: source_user?.rubi_user?.last_name,
              org: source_org?.title,
            })}
          </Typography>
          <br></br>
          <Typography variant="body2">
            <strong>{t("destination_group")}:</strong>{" "}
            {destination_group?.name || "-"}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          <strong>{t("time_expires")}:</strong>{" "}
          {time_expires ? new Date(time_expires).toLocaleString() : "-"}
        </Typography>
      </CardContent>
      {is_moderation_enabled && (
        <CardActions
          sx={{ justifyContent: "flex-end", mt: "auto", pb: 2, pr: 2 }}
        >
          {onAdmit && (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onAdmit(id);
              }}
              sx={{
                mr: 1,
                borderRadius: 2,
                fontWeight: 600,
                boxShadow: 1,
                textTransform: "none",
              }}
            >
              {t("admit")}
            </Button>
          )}
          {onReject && (
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onReject(id);
              }}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                boxShadow: 1,
                textTransform: "none",
              }}
            >
              {t("reject")}
            </Button>
          )}
        </CardActions>
      )}
    </Card>
  );
};

export default BulletinShareCard;
